import { createContext, useEffect, useLayoutEffect } from 'react'

type OpenListener = (openedId: string) => void

/**
 * How the items of one accordion tell each other that one of them opened.
 * Each item keeps its own open state; the bus only carries the news, so that
 * the others can close themselves.
 *
 * The state stays in the items: the root can't read `expanded` on an item
 * returned from another component, and an item that renders its own starting
 * state is already open in the server HTML. Only a click announces, never a
 * mount, so no item that starts expanded closes as the page hydrates.
 *
 * Internal: it is not exported from the item's barrel, so it never becomes
 * part of the library's public API. It imports nothing from the item or the
 * root, so neither import creates a cycle.
 */
export type AccordionBus = {
  /** Calls the listener with the id of each item that opens from now on. */
  subscribe: (listener: OpenListener) => () => void

  /** Tells every listening item that the item with this id opened. */
  announceOpen: (id: string) => void
}

export const createAccordionBus = (): AccordionBus => {
  const listeners = new Set<OpenListener>()

  return {
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    announceOpen: (id) => {
      listeners.forEach((listener) => listener(id))
    },
  }
}

export const AccordionBusContext = createContext<AccordionBus | null>(null)

/**
 * A layout effect in the browser and a plain effect on the server. React 18
 * warns about `useLayoutEffect` during server rendering, where no effect runs
 * anyway, and a plain effect in the browser would leave a gap after mount in
 * which a click could arrive before an item listens.
 */
export const useClientLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect
